import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  BookOpenIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  StarIcon,
  ClockIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ArrowRightIcon,
  TagIcon
} from '@heroicons/react/24/outline';

const CourseCatalog = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedDuration, setSelectedDuration] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const categories = [
    { id: 'all', name: 'All Categories', count: 45 },
    { id: 'management', name: 'Healthcare Management', count: 12 },
    { id: 'clinical', name: 'Clinical Skills', count: 8 },
    { id: 'technology', name: 'Medical Technology', count: 7 },
    { id: 'ethics', name: 'Medical Ethics', count: 5 },
    { id: 'emergency', name: 'Emergency Care', count: 6 },
    { id: 'communication', name: 'Patient Communication', count: 4 },
    { id: 'analytics', name: 'Healthcare Analytics', count: 3 }
  ];

  const difficulties = [
    { id: 'all', name: 'All Levels' },
    { id: 'beginner', name: 'Beginner' },
    { id: 'intermediate', name: 'Intermediate' },
    { id: 'advanced', name: 'Advanced' }
  ];

  const durations = [
    { id: 'all', name: 'Any Duration' },
    { id: 'short', name: '1-3 weeks' },
    { id: 'medium', name: '4-6 weeks' },
    { id: 'long', name: '7+ weeks' }
  ];

  const sortOptions = [
    { id: 'popular', name: 'Most Popular' },
    { id: 'newest', name: 'Newest First' },
    { id: 'rating', name: 'Highest Rated' },
    { id: 'alphabetical', name: 'A-Z' }
  ];

  // Mock courses data
  const allCourses = useMemo(() => [
    {
      id: 1,
      title: 'Advanced Healthcare Management',
      instructor: 'Dr. Sarah Wilson',
      category: 'management',
      difficulty: 'advanced',
      duration: '8 weeks',
      durationWeeks: 8,
      rating: 4.8,
      reviews: 247,
      students: 1523,
      price: 299,
      description: 'Comprehensive course covering strategic planning, quality improvement, and leadership in healthcare organizations.',
      skills: ['Strategic Planning', 'Quality Management', 'Healthcare Leadership', 'Risk Assessment'],
      image: '/images/course-healthcare-mgmt.jpg',
      popular: true,
      new: false,
      featured: true
    },
    {
      id: 2,
      title: 'Medical Ethics and Patient Care',
      instructor: 'Prof. Michael Chen',
      category: 'ethics',
      difficulty: 'intermediate',
      duration: '6 weeks',
      durationWeeks: 6,
      rating: 4.9,
      reviews: 189,
      students: 987,
      price: 199,
      description: 'Explore ethical principles, informed consent, patient rights, and moral decision-making in healthcare.',
      skills: ['Medical Ethics', 'Patient Rights', 'Informed Consent', 'Moral Reasoning'],
      image: '/images/course-medical-ethics.jpg',
      popular: true,
      new: false,
      featured: false
    },
    {
      id: 3,
      title: 'Emergency Response Protocols',
      instructor: 'Dr. Lisa Rodriguez',
      category: 'emergency',
      difficulty: 'advanced',
      duration: '4 weeks',
      durationWeeks: 4,
      rating: 4.7,
      reviews: 156,
      students: 743,
      price: 249,
      description: 'Master emergency response procedures, crisis management, and rapid decision-making in critical situations.',
      skills: ['Emergency Protocols', 'Crisis Management', 'Rapid Assessment', 'Team Coordination'],
      image: '/images/course-emergency.jpg',
      popular: false,
      new: true,
      featured: false
    },
    {
      id: 4,
      title: 'Digital Health Technologies',
      instructor: 'Dr. James Park',
      category: 'technology',
      difficulty: 'beginner',
      duration: '5 weeks',
      durationWeeks: 5,
      rating: 4.6,
      reviews: 134,
      students: 1247,
      price: 179,
      description: 'Learn about the latest digital health innovations, telemedicine, and their implementation in healthcare.',
      skills: ['Digital Health', 'Telemedicine', 'Health Apps', 'Technology Implementation'],
      image: '/images/course-digital-health.jpg',
      popular: true,
      new: false,
      featured: false
    },
    {
      id: 5,
      title: 'Healthcare Data Analytics',
      instructor: 'Prof. Anna Kumar',
      category: 'analytics',
      difficulty: 'intermediate',
      duration: '7 weeks',
      durationWeeks: 7,
      rating: 4.8,
      reviews: 98,
      students: 892,
      price: 279,
      description: 'Master data analysis techniques for healthcare decision-making, predictive modeling, and performance metrics.',
      skills: ['Data Analysis', 'Predictive Modeling', 'Healthcare Metrics', 'Statistical Analysis'],
      image: '/images/course-data-analytics.jpg',
      popular: false,
      new: true,
      featured: true
    },
    {
      id: 6,
      title: 'Patient Communication Skills',
      instructor: 'Dr. Emily Thompson',
      category: 'communication',
      difficulty: 'beginner',
      duration: '3 weeks',
      durationWeeks: 3,
      rating: 4.9,
      reviews: 312,
      students: 2156,
      price: 149,
      description: 'Enhance your patient interaction and communication abilities with practical techniques and real-world scenarios.',
      skills: ['Patient Communication', 'Active Listening', 'Empathy Training', 'Difficult Conversations'],
      image: '/images/course-communication.jpg',
      popular: true,
      new: false,
      featured: false
    },
    {
      id: 7,
      title: 'Clinical Decision Making',
      instructor: 'Dr. Robert Kim',
      category: 'clinical',
      difficulty: 'advanced',
      duration: '6 weeks',
      durationWeeks: 6,
      rating: 4.7,
      reviews: 76,
      students: 654,
      price: 259,
      description: 'Develop critical thinking skills for clinical assessment, diagnosis, and treatment planning.',
      skills: ['Clinical Assessment', 'Diagnostic Reasoning', 'Treatment Planning', 'Evidence-Based Practice'],
      image: '/images/course-clinical-decision.jpg',
      popular: false,
      new: true,
      featured: false
    },
    {
      id: 8,
      title: 'Healthcare Quality Improvement',
      instructor: 'Dr. Maria Garcia',
      category: 'management',
      difficulty: 'intermediate',
      duration: '5 weeks',
      durationWeeks: 5,
      rating: 4.6,
      reviews: 89,
      students: 567,
      price: 219,
      description: 'Learn systematic approaches to improving healthcare quality, patient safety, and operational efficiency.',
      skills: ['Quality Improvement', 'Patient Safety', 'Process Optimization', 'Performance Metrics'],
      image: '/images/course-quality.jpg',
      popular: false,
      new: false,
      featured: false
    }
  ], []);

  const [filteredCourses, setFilteredCourses] = useState(allCourses);

  useEffect(() => {
    // Simulate loading
    setTimeout(() => setIsLoading(false), 800);
  }, []);

  useEffect(() => {
    let filtered = allCourses;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.instructor.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.skills.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(course => course.category === selectedCategory);
    }

    // Filter by difficulty
    if (selectedDifficulty !== 'all') {
      filtered = filtered.filter(course => course.difficulty === selectedDifficulty);
    }

    // Filter by duration
    if (selectedDuration !== 'all') {
      filtered = filtered.filter(course => {
        switch (selectedDuration) {
          case 'short': return course.durationWeeks <= 3;
          case 'medium': return course.durationWeeks >= 4 && course.durationWeeks <= 6;
          case 'long': return course.durationWeeks >= 7;
          default: return true;
        }
      });
    }

    // Sort courses
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'popular':
          return b.students - a.students;
        case 'newest':
          return b.new - a.new;
        case 'rating':
          return b.rating - a.rating;
        case 'alphabetical':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    setFilteredCourses(filtered);
  }, [searchTerm, selectedCategory, selectedDifficulty, selectedDuration, sortBy, allCourses]);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedDifficulty('all');
    setSelectedDuration('all');
    setSortBy('popular');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-accent"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Page Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 flex items-center">
                <BookOpenIcon className="h-6 w-6 sm:h-8 sm:w-8 text-apollo-accent mr-2 sm:mr-3" />
                Course Catalog
              </h1>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
                Discover and enroll in professional development courses
              </p>
            </div>
            
            <div className="flex items-center justify-end space-x-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center text-sm"
              >
                <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Filters</span>
                <span className="sm:hidden">Filter</span>
                <ChevronDownIcon className={`h-3 w-3 sm:h-4 sm:w-4 ml-1 sm:ml-2 transform transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-4 sm:mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search courses, instructors, or skills..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-accent focus:border-transparent text-sm sm:text-base"
            />
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* Category Filter */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-2 sm:px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-apollo-accent focus:border-transparent"
                >
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name} ({category.count})
                    </option>
                  ))}
                </select>
              </div>

              {/* Difficulty Filter */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Difficulty</label>
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-2 sm:px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-apollo-accent focus:border-transparent"
                >
                  {difficulties.map(difficulty => (
                    <option key={difficulty.id} value={difficulty.id}>
                      {difficulty.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Duration Filter */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Duration</label>
                <select
                  value={selectedDuration}
                  onChange={(e) => setSelectedDuration(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-2 sm:px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-apollo-accent focus:border-transparent"
                >
                  {durations.map(duration => (
                    <option key={duration.id} value={duration.id}>
                      {duration.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort Filter */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-2 sm:px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-apollo-accent focus:border-transparent"
                >
                  {sortOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 sm:mt-6">
              <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-0">
                Showing {filteredCourses.length} of {allCourses.length} courses
              </p>
              <button
                onClick={clearFilters}
                className="text-apollo-accent hover:text-apollo-accent/80 text-xs sm:text-sm font-medium self-start sm:self-auto"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}

        {/* Course Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredCourses.map((course) => (
            <div key={course.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-200">
              {/* Course Image */}
              <div className="relative h-36 sm:h-48 bg-gradient-to-br from-apollo-accent to-blue-600">
                <div className="absolute inset-0 flex items-center justify-center">
                  <BookOpenIcon className="h-12 w-12 sm:h-16 sm:w-16 text-white opacity-50" />
                </div>
                
                {/* Badges */}
                <div className="absolute top-2 sm:top-4 left-2 sm:left-4 flex flex-wrap gap-1 sm:gap-2">
                  {course.featured && (
                    <span className="bg-yellow-500 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium">
                      Featured
                    </span>
                  )}
                  {course.new && (
                    <span className="bg-green-500 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium">
                      New
                    </span>
                  )}
                  {course.popular && (
                    <span className="bg-purple-500 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium">
                      Popular
                    </span>
                  )}
                </div>

                {/* Difficulty Badge */}
                <div className="absolute top-2 sm:top-4 right-2 sm:right-4">
                  <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium ${getDifficultyColor(course.difficulty)}`}>
                    {course.difficulty.charAt(0).toUpperCase() + course.difficulty.slice(1)}
                  </span>
                </div>
              </div>

              {/* Course Content */}
              <div className="p-4 sm:p-6">
                <div className="flex items-start justify-between mb-2 sm:mb-3">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 line-clamp-2">
                    {course.title}
                  </h3>
                </div>

                <p className="text-xs sm:text-sm text-gray-600 mb-2">
                  by {course.instructor}
                </p>

                <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 line-clamp-2">
                  {course.description}
                </p>

                {/* Course Stats */}
                <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                  <div className="flex items-center">
                    <StarIcon className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-400 mr-1" />
                    <span className="font-medium">{course.rating}</span>
                    <span className="ml-1 hidden sm:inline">({course.reviews})</span>
                  </div>
                  
                  <div className="flex items-center">
                    <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span>{course.duration}</span>
                  </div>
                  
                  <div className="flex items-center">
                    <UserGroupIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span className="hidden sm:inline">{course.students.toLocaleString()}</span>
                    <span className="sm:hidden">{(course.students / 1000).toFixed(0)}k</span>
                  </div>
                </div>

                {/* Skills Tags */}
                <div className="mb-3 sm:mb-4">
                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {course.skills.slice(0, 3).map((skill, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs flex items-center">
                        <TagIcon className="h-3 w-3 mr-1" />
                        {skill}
                      </span>
                    ))}
                    {course.skills.length > 3 && (
                      <span className="bg-gray-100 text-gray-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">
                        +{course.skills.length - 3}
                      </span>
                    )}
                  </div>
                </div>

                {/* Price and CTA */}
                <div className="flex items-center justify-between">
                  <div className="text-lg sm:text-2xl font-bold text-gray-900">
                    ${course.price}
                  </div>
                  
                  <button
                    onClick={() => navigate(`/training/course/${course.id}`)}
                    className="bg-apollo-accent text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-apollo-accent/90 transition-colors duration-200 flex items-center text-xs sm:text-sm font-medium"
                  >
                    View Course
                    <ArrowRightIcon className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredCourses.length === 0 && (
          <div className="text-center py-8 sm:py-12">
            <BookOpenIcon className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No courses found</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-4">
              Try adjusting your search criteria or filters to find more courses.
            </p>
            <button
              onClick={clearFilters}
              className="bg-apollo-accent text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-apollo-accent/90 transition-colors duration-200 text-sm sm:text-base"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Load More Button */}
        {filteredCourses.length > 0 && filteredCourses.length >= 9 && (
          <div className="text-center mt-8 sm:mt-12">
            <button className="bg-white border border-gray-300 text-gray-700 px-6 sm:px-8 py-2 sm:py-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center mx-auto text-sm sm:text-base">
              Load More Courses
              <ArrowRightIcon className="h-4 w-4 ml-2" />
            </button>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CourseCatalog;
